import type { SignificanceDisplayItem } from '@/state/experiments/useExperimentStats';
import type { SignificanceExplanation, Verdict } from './significance-explanation';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { IconCircleCheck, IconCircleX, IconHelpCircle, IconMinus, IconChevronDown } from '@tabler/icons-react';
import { cn } from 'helpers/cn';
import { COLUMN_GLOSSARY, explainSignificance } from './significance-explanation';

/**
 * Plain-language reading of the table above, one row at a time.
 *
 * The table answers "what are the numbers"; this answers "so what". The verdict
 * colour matches the table's own badges so the two cannot be read as disagreeing.
 */
type SignificanceExplainerProps = {
  significance: readonly SignificanceDisplayItem[];
  evaluationMethod: 'frequentist' | 'bayesian';
};

const VERDICT_STYLE: Record<Verdict, { icon: typeof IconCircleCheck; className: string; label: string }> = {
  winner: { icon: IconCircleCheck, className: 'text-ok', label: 'Winning' },
  loser: { icon: IconCircleX, className: 'text-danger', label: 'Losing' },
  inconclusive: { icon: IconMinus, className: 'text-warn', label: 'Not yet decided' },
  unknown: { icon: IconHelpCircle, className: 'text-dim', label: 'Too early' },
};

const ExplanationRow = ({ explanation }: { explanation: SignificanceExplanation }) => {
  const { icon: Icon, className, label } = VERDICT_STYLE[explanation.verdict];

  return (
    <div className="flex gap-2.5">
      <Icon className={cn('mt-0.5 size-4 shrink-0', className)} />
      <div className="space-y-0.5">
        <p className="text-body leading-relaxed">
          <span className={cn('micro-label mr-1.5', className)}>{label}</span>
          <span className="text-foreground">{explanation.reading}</span>
        </p>
        <p className="supporting">{explanation.advice}</p>
      </div>
    </div>
  );
};

export const SignificanceExplainer = ({ significance, evaluationMethod }: SignificanceExplainerProps) => {
  const [showGlossary, setShowGlossary] = useState(false);

  if (!significance || significance.length === 0) return null;

  const explanations = explainSignificance(significance, evaluationMethod);
  const glossary = COLUMN_GLOSSARY[evaluationMethod];

  return (
    <div className="border-line mt-5 space-y-3 border-t pt-5">
      <h4 className="micro-label">What this means</h4>

      <div className="space-y-3">
        {explanations.map((explanation) => (
          <ExplanationRow key={explanation.id} explanation={explanation} />
        ))}
      </div>

      {/*
        The glossary is collapsed: it is reference material for a first read, and
        left open it buries the per-variant readings that are the point.
      */}
      <Button
        variant="ghost"
        size="sm"
        className="text-dim hover:bg-hover hover:text-foreground -ml-2 h-8 rounded-lg font-mono text-micro"
        onClick={() => setShowGlossary((open) => !open)}
      >
        <IconChevronDown className={cn('mr-1 size-3.5 transition-transform', showGlossary && 'rotate-180')} />
        {showGlossary ? 'Hide' : 'What do the columns mean?'}
      </Button>

      {showGlossary && (
        <dl className="border-line bg-panel rounded-card grid gap-3 border p-3 sm:grid-cols-2">
          {glossary.map((entry) => (
            <div key={entry.term} className="space-y-0.5">
              <dt className="micro-label">{entry.term}</dt>
              <dd className="supporting">{entry.meaning}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
};
